import { useTranslation } from 'react-i18next'
import { UnionBill, ProductionAssignedActionType, ProductionAssignedReturn } from './production-assigned-actions.type'
import { ReservationF } from '../../../../types/reservation-detail.types'
import { useMemo } from 'react'
import { ProductionAssignedActionMove } from './production-assigned-action-move'
import { ProductionAssignedActionSplit } from './production-assigned-action-split'
import useErrorSnackBar from 'src/app/hooks/error-snack-bar/error-snack-bar'
import { ProductionAssignedActionDelete } from './production-assigned-action-delete'
import { ProductionAssignedActionEditCityTax } from './production-assigned-action-edit-city-tax'

type ProductionAssignedMoveDialogProps = {
  action: ProductionAssignedActionType
  productionAssignedIds: string[]
  bills: UnionBill[]
  open: boolean
  onClose: () => void
  reservation?: ReservationF
}

export function ProductionAssignedActionsDialog({ action, productionAssignedIds, bills, open, onClose, reservation }: ProductionAssignedMoveDialogProps) {
  const { t } = useTranslation()
  const showError = useErrorSnackBar()

  const title = useMemo(() => {
    switch (action) {
      case 'MOVE':
        return t('pms.booking.reservation.productions_assigned.action.move.title')
      case 'SPLIT':
        return t('pms.booking.reservation.productions_assigned.action.split.title')
      case 'DELETE':
        return t('pms.booking.reservation.productions_assigned.action.delete.title')
      case 'EDIT_CITY_TAX':
        return t('pms.booking.reservation.productions_assigned.action.edit_city_tax.title')
    }
  }, [action])

  const parsedBills = useMemo(() => {
    if (bills && productionAssignedIds) {
      return parseBills(bills, productionAssignedIds)
    } else {
      return undefined
    }
  }, [bills, productionAssignedIds])

  switch (action) {
    case 'MOVE':
      if (parsedBills) {
        const { sourceBill, destinationBills } = parsedBills
        if (sourceBill?.reservationId)
          return (
            <ProductionAssignedActionMove //
              title={title}
              open={open}
              onClose={onClose}
              bills={destinationBills.filter((x) => x.open)}
              originBillCategory={sourceBill.category}
              productionAssignedIds={productionAssignedIds}
              reservationId={sourceBill.reservationId}
              reservedResourceId={sourceBill.reservedResourceId ?? undefined}
              reservation={reservation}
              sourceBillId={sourceBill.id}
            />
          )
        else {
          showError('Errore')
          console.error('Source bill missing reserved resource id!', { billId: sourceBill?.id, reservedResourceId: sourceBill?.reservedResourceId, reservationId: sourceBill?.reservationId })
        }
      }
      return <></>

    case 'SPLIT':
      if (parsedBills) {
        const { sourceBill, destinationBills } = parsedBills
        if (sourceBill?.reservationId)
          return (
            <ProductionAssignedActionSplit //
              title={title}
              open={open}
              onClose={onClose}
              bills={bills}
              productionAssignedIds={productionAssignedIds}
              originBillCategory={sourceBill.category}
              getProductionAssigned={getProductionAssigned}
              reservationId={sourceBill.reservationId}
              reservedResourceId={sourceBill.reservedResourceId ?? undefined}
              reservation={reservation}
            />
          )
        else {
          showError('Errore')
          console.error('Source bill missing reserved resource id!', { billId: sourceBill?.id, reservedResourceId: sourceBill?.reservedResourceId, reservationId: sourceBill?.reservationId })
        }
      }
      return <></>
    case 'DELETE':
      return (
        <ProductionAssignedActionDelete //
          title={title}
          open={open}
          onClose={onClose}
          bills={bills}
          productionAssignedIds={productionAssignedIds}
          getProductionAssigned={getProductionAssigned}
        />
      )

    case 'EDIT_CITY_TAX':
      return (
        <ProductionAssignedActionEditCityTax title={title} open={open} onClose={onClose} bills={bills} productionAssignedIds={productionAssignedIds} getProductionAssigned={getProductionAssigned} />
      )
  }
}

function getProductionAssigned(bills: UnionBill[], productionAssignedId: string): ProductionAssignedReturn {
  return bills.reduce((i: ProductionAssignedReturn, c: UnionBill) => {
    let productionAssigned = undefined
    // check needed because in the console it was throwing an error
    // This expression is not callable. Each member of the union type ... has signatures, but none of those signatures are compatible with each other.
    if ('ownerId' in c) {
      productionAssigned = (c.productions ?? []).find((p) => p.id === productionAssignedId)
    } else {
      productionAssigned = (c.productions ?? []).find((p) => p.id === productionAssignedId)
    }
    if (productionAssigned) return productionAssigned
    else return i
  }, undefined)
}

function getProductionAssignedBillId(bills: UnionBill[], productionAssignedId: string) {
  return bills.find((b) => b.productions?.map((p) => p.id).includes(productionAssignedId))?.id ?? undefined
}

function getBillOfProductionsAssigned(bills: UnionBill[], productionAssignedIds: string[]) {
  if (productionAssignedIds.length === 1) return getProductionAssignedBillId(bills, productionAssignedIds[0])
  const paMap = bills.flatMap((b) => {
    return b.productions?.map((p) => ({ productionId: p.id, billId: p.billId })) ?? []
  })

  const productionsAssignedWithBill = paMap.filter((x) => productionAssignedIds.includes(x.productionId))

  if (productionsAssignedWithBill.length > 0 && productionsAssignedWithBill.every((x) => x.billId === productionsAssignedWithBill[0].billId)) return productionsAssignedWithBill[0].billId
  else return undefined
}

function parseBills(bills: UnionBill[], productionAssignedIds: string[]) {
  const currentProductionsAssignedBillId = getBillOfProductionsAssigned(bills, productionAssignedIds)
  if (currentProductionsAssignedBillId) {
    return {
      sourceBill: bills.find((x) => x.id === currentProductionsAssignedBillId),
      destinationBills: bills.filter((b) => b.id !== currentProductionsAssignedBillId),
    }
  } else return undefined
}
